"use client";

import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import type { PipSnapConfig } from "./pip-config";
import { DEFAULT_PIP_SNAP_CONFIG } from "./pip-config";
import type {
	PipBounds,
	PipDimensions,
	PipPosition,
	PipSnapInsets,
} from "./pip-snap";
import {
	clampPipPosition,
	pipPositionsEqual,
	resolvePipDefaultPosition,
	snapPipPosition,
} from "./pip-snap";

export interface FloatingPipDragHandlers {
	onPointerDown: React.PointerEventHandler<HTMLElement>;
	onPointerMove: React.PointerEventHandler<HTMLElement>;
	onPointerUp: React.PointerEventHandler<HTMLElement>;
}

export interface UseFloatingPipDragOptions {
	enabled: boolean;
	stageRef: React.RefObject<HTMLElement | null>;
	pipDimensions: PipDimensions;
	config?: PipSnapConfig;
	/** Extra clamp/snap padding merged on top of document safe-area insets. */
	snapInsets?: PipSnapInsets;
}

export interface UseFloatingPipDragResult {
	position: PipPosition;
	isDragging: boolean;
	isSnapping: boolean;
	onPointerDown: FloatingPipDragHandlers["onPointerDown"];
	onPointerMove: FloatingPipDragHandlers["onPointerMove"];
	onPointerUp: FloatingPipDragHandlers["onPointerUp"];
	resetToDefault: () => void;
	setPosition: (position: PipPosition) => void;
}

function readDocumentSafeAreaInsets(): {
	top: number;
	right: number;
	bottom: number;
	left: number;
} {
	const style = getComputedStyle(document.documentElement);
	const toPx = (value: string) => Number.parseFloat(value) || 0;
	return {
		top: toPx(style.getPropertyValue("env(safe-area-inset-top)")),
		right: toPx(style.getPropertyValue("env(safe-area-inset-right)")),
		bottom: toPx(style.getPropertyValue("env(safe-area-inset-bottom)")),
		left: toPx(style.getPropertyValue("env(safe-area-inset-left)")),
	};
}

function mergePipInsets(
	safeArea: PipSnapInsets,
	extra?: PipSnapInsets,
): PipSnapInsets {
	return {
		top: (safeArea.top ?? 0) + (extra?.top ?? 0),
		right: (safeArea.right ?? 0) + (extra?.right ?? 0),
		bottom: (safeArea.bottom ?? 0) + (extra?.bottom ?? 0),
		left: (safeArea.left ?? 0) + (extra?.left ?? 0),
	};
}

function resolveSnapInsetScalar(
	insets: PipSnapInsets | undefined,
	edge: keyof PipSnapInsets,
): number {
	return insets?.[edge] ?? 0;
}

export function useFloatingPipDrag(
	options: UseFloatingPipDragOptions,
): UseFloatingPipDragResult {
	const {
		enabled,
		stageRef,
		pipDimensions,
		config = DEFAULT_PIP_SNAP_CONFIG,
		snapInsets,
	} = options;
	const [position, setPositionState] = useState<PipPosition>({
		x: config.edgePaddingPx,
		y: config.edgePaddingPx,
	});
	const [isDragging, setIsDragging] = useState(false);
	const [isSnapping, setIsSnapping] = useState(false);
	const dragOffsetRef = useRef<PipPosition>({ x: 0, y: 0 });
	const snapInsetsRef = useRef(snapInsets);
	const pipDimensionsRef = useRef(pipDimensions);
	const configRef = useRef(config);

	snapInsetsRef.current = snapInsets;
	pipDimensionsRef.current = pipDimensions;
	configRef.current = config;

	const setPosition = useCallback((next: PipPosition) => {
		setPositionState((current) =>
			pipPositionsEqual(current, next) ? current : next,
		);
	}, []);

	const snapInsetTop = resolveSnapInsetScalar(snapInsets, "top");
	const snapInsetRight = resolveSnapInsetScalar(snapInsets, "right");
	const snapInsetBottom = resolveSnapInsetScalar(snapInsets, "bottom");
	const snapInsetLeft = resolveSnapInsetScalar(snapInsets, "left");

	const readBounds = useCallback((): PipBounds | null => {
		const element = stageRef.current;
		if (!element) {
			return null;
		}
		const rect = element.getBoundingClientRect();
		return { width: rect.width, height: rect.height };
	}, [stageRef]);

	const readSnapInsets = useCallback((): PipSnapInsets => {
		return mergePipInsets(
			readDocumentSafeAreaInsets(),
			snapInsetsRef.current,
		);
	}, []);

	const clampCurrentPosition = useCallback(() => {
		const bounds = readBounds();
		if (!bounds) {
			return;
		}
		setPositionState((current) => {
			const next = clampPipPosition({
				position: current,
				bounds,
				pipDimensions: pipDimensionsRef.current,
				insets: readSnapInsets(),
				config: configRef.current,
			});
			return pipPositionsEqual(current, next) ? current : next;
		});
	}, [readBounds, readSnapInsets]);

	const resetToDefault = useCallback(() => {
		const bounds = readBounds();
		if (!bounds) {
			return;
		}
		const next = resolvePipDefaultPosition({
			bounds,
			pipDimensions: pipDimensionsRef.current,
			insets: readSnapInsets(),
			config: configRef.current,
		});
		setPosition(next);
	}, [readBounds, readSnapInsets, setPosition]);

	const resetToDefaultRef = useRef(resetToDefault);
	resetToDefaultRef.current = resetToDefault;

	useEffect(() => {
		if (!enabled) {
			return;
		}
		resetToDefaultRef.current();
	}, [enabled]);

	useLayoutEffect(() => {
		if (!enabled || isDragging) {
			return;
		}
		clampCurrentPosition();
	}, [
		clampCurrentPosition,
		enabled,
		isDragging,
		pipDimensions.heightPx,
		pipDimensions.widthPx,
		snapInsetBottom,
		snapInsetLeft,
		snapInsetRight,
		snapInsetTop,
	]);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		const element = stageRef.current;
		if (!element || typeof ResizeObserver === "undefined") {
			return;
		}

		const handleStageResize = () => {
			if (isDragging) {
				return;
			}
			clampCurrentPosition();
		};

		const observer = new ResizeObserver(handleStageResize);
		observer.observe(element);
		window.addEventListener("resize", handleStageResize);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", handleStageResize);
		};
	}, [clampCurrentPosition, enabled, isDragging, stageRef]);

	const onPointerDown = useCallback(
		(event: React.PointerEvent<HTMLElement>) => {
			if (!enabled) {
				return;
			}
			const bounds = readBounds();
			if (!bounds) {
				return;
			}
			const target = event.currentTarget;
			target.setPointerCapture(event.pointerId);
			const stageRect = stageRef.current?.getBoundingClientRect();
			if (!stageRect) {
				return;
			}
			dragOffsetRef.current = {
				x: event.clientX - stageRect.left - position.x,
				y: event.clientY - stageRect.top - position.y,
			};
			setIsDragging(true);
			setIsSnapping(false);
		},
		[enabled, position.x, position.y, readBounds, stageRef],
	);

	const onPointerMove = useCallback(
		(event: React.PointerEvent<HTMLElement>) => {
			if (!isDragging) {
				return;
			}
			const bounds = readBounds();
			if (!bounds) {
				return;
			}
			const stageRect = stageRef.current?.getBoundingClientRect();
			if (!stageRect) {
				return;
			}
			const next = clampPipPosition({
				position: {
					x: event.clientX - stageRect.left - dragOffsetRef.current.x,
					y: event.clientY - stageRect.top - dragOffsetRef.current.y,
				},
				bounds,
				pipDimensions: pipDimensionsRef.current,
				insets: readSnapInsets(),
				config: configRef.current,
			});
			setPosition(next);
		},
		[isDragging, readBounds, readSnapInsets, setPosition, stageRef],
	);

	const onPointerUp = useCallback(
		(event: React.PointerEvent<HTMLElement>) => {
			if (!isDragging) {
				return;
			}
			event.currentTarget.releasePointerCapture(event.pointerId);
			setIsDragging(false);
			const bounds = readBounds();
			if (!bounds) {
				return;
			}
			setIsSnapping(true);
			setPosition(
				snapPipPosition({
					position,
					bounds,
					pipDimensions: pipDimensionsRef.current,
					insets: readSnapInsets(),
					config: configRef.current,
				}),
			);
			window.setTimeout(
				() => setIsSnapping(false),
				configRef.current.snapTransitionMs,
			);
		},
		[isDragging, position, readBounds, readSnapInsets, setPosition],
	);

	return {
		position,
		isDragging,
		isSnapping,
		onPointerDown,
		onPointerMove,
		onPointerUp,
		resetToDefault,
		setPosition,
	};
}
